/**
 * Shared error handling utilities for API errors
 * Centralized definitions to avoid duplication across multiple files
 */

// Error type definitions
export interface ApiErrorResponse {
  message?: string;
  errors?: Record<string, string[]>;
  statusCode?: number;
}

export interface ApiError extends Error {
  response?: {
    status: number;
    statusText?: string;
    data?: ApiErrorResponse;
  };
  config?: {
    url?: string;
    method?: string;
  };
}

// Type guard to check if error is ApiError
export function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as Error).message === 'string'
  );
}

// Helper to convert any error to ApiError
export function toApiError(error: unknown): ApiError {
  if (isApiError(error)) {
    return error;
  }

  // Create a new ApiError from unknown error
  const apiError = new Error('Unknown error occurred') as ApiError;
  if (error && typeof error === 'object' && 'message' in error) {
    apiError.message = String(error.message);
  }
  return apiError;
}
